import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageSquare, ClipboardList, Users, Rocket, ArrowRight } from "lucide-react";

export const HowItWorksPage = () => {
  const steps = [
    { icon: MessageSquare, num: "01", title: "Discovery", desc: "An initial strategy session identifies operational challenges, priorities, existing systems, communication requirements, and workflow gaps.", details: ["Identify gaps", "Understand priorities", "Map workflows", "Review systems"] },
    { icon: ClipboardList, num: "02", title: "Custom Scope Design", desc: "An operational structure is defined around the requirements identified during discovery.", details: ["Define scope", "Establish ownership boundaries", "Determine protocols", "Identify required systems"] },
    { icon: Users, num: "03", title: "Onboarding & Integration", desc: "A structured onboarding process establishes access, workflows, documentation, communication protocols, and operating standards.", details: ["Tool access", "Preference documentation", "Workflow setup", "First-week alignment"] },
    { icon: Rocket, num: "04", title: "Ongoing Operations", desc: "Consistent execution, communication, monitoring, and continuous optimization keep priorities clear and operations moving.", details: ["Alignment", "Proactive updates", "Execution", "Continuous optimization"] }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl text-[#2D4A6D] mb-6 font-serif">How It Works</h1>
          <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto mb-6" />
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            A clear, structured path from initial conversation to ongoing operational partnership.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {steps.map((step, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 py-12 ${i < steps.length - 1 ? "border-b border-slate-100" : ""}`}>
                <div className="lg:col-span-2 flex lg:flex-col items-center lg:items-start gap-4">
                  <div className="w-20 h-20 bg-[#2D4A6D] flex items-center justify-center flex-shrink-0">
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <span className="text-5xl font-light text-slate-200 hidden lg:block">{step.num}</span>
                </div>
                <div className="lg:col-span-10 lg:pl-8">
                  <h3 className="text-2xl font-semibold text-[#2D4A6D] mb-4">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed mb-6 max-w-2xl">{step.desc}</p>
                  <div className="flex flex-wrap gap-3">
                    {step.details.map((detail, j) => (
                      <span key={j} className="text-sm text-slate-600 bg-slate-50 border border-slate-200 px-4 py-2">{detail}</span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What You Can Expect */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl text-[#2D4A6D] mb-4 font-serif">What You Can Expect</h2>
            <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: "Same-Day Response", desc: "Priority communications addressed within agreed business hours." },
              { title: "Weekly Alignment", desc: "Consistent alignment to keep priorities visible and synchronized." },
              { title: "Proactive Updates", desc: "Status remains visible without requiring repeated follow-up." },
              { title: "Documentation", desc: "Systems designed to capture preferences, processes, and operating requirements." },
              { title: "Discretion", desc: "Confidentiality and discretion across operational matters." },
              { title: "Ownership Mindset", desc: "Responsibilities are managed with attention to completion, quality, and follow-through." }
            ].map((item, i) => (
              <div key={i} className="p-6 bg-white border border-slate-200">
                <h4 className="text-base font-semibold text-[#2D4A6D] mb-2">{item.title}</h4>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2D4A6D] text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl mb-6 font-serif">Ready to Get Started?</h2>
          <div className="w-16 h-0.5 bg-white/30 mx-auto mb-8" />
          <p className="text-slate-300 mb-10">The first step is a focused conversation to understand the operational requirements and determine the appropriate structure.</p>
          <Link to="/book">
            <button className="bg-white text-[#2D4A6D] hover:bg-slate-100 px-10 py-4 font-medium flex items-center mx-auto">
              Book a Strategy Call <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};
